/**
 * Sidebar Component
 * Enterprise-grade navigation sidebar with collapsible sections
 * Mobile-responsive with hamburger menu
 */

import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { getVillaPickerOptions, getCalendarAlertSummary } from '../services/api'
import { listLeads } from '../services/leadsApi'
import { useAuth } from '../contexts/AuthContext'
import { getReservationAgentLabels, isReservationAgentRole } from '../utils/reservationAgentAccess'
import { ROLES, OPERATIONAL_ROLES, hasRole, isManagement, isReservationManager } from '../constants/roles'
import { resolveMediaUrl } from '../utils/mediaUrl'
import LogoFull from './LogoFull'
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  LayoutDashboard,
  Building2,
  Home,
  Users,
  Target,
  Wallet,
  Receipt,
  CreditCard,
  FileBarChart,
  Settings,
  Palette,
  FileSpreadsheet,
  Download,
  User,
  Briefcase,
  Menu,
  X,
  Inbox,
  Mail,
  MessageCircle,
  PenLine,
  CalendarDays,
  ScrollText,
  RefreshCw,
} from 'lucide-react'

// UserAvatar component with error handling
const UserAvatar = ({ user, size = 'md' }) => {
  const [imgError, setImgError] = useState(false)

  // A previously-failed avatar must retry when the URL changes (e.g. the
  // user uploads a new photo) — otherwise the initials fallback sticks
  // until a full page reload.
  useEffect(() => {
    setImgError(false)
  }, [user?.avatar_url])

  const sizeClasses = {
    sm: 'w-9 h-9 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg'
  }

  const initial = user?.full_name?.charAt(0) || user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'
  const imgSrc = resolveMediaUrl(user?.avatar_url)
  const hasAvatar = !!imgSrc && !imgError

  if (hasAvatar) {
    return (
      <div className={`${sizeClasses[size]} rounded-full flex-shrink-0 group-hover:ring-2 group-hover:ring-[#cfa190] dark:group-hover:ring-[#1e2a2e] transition-all overflow-hidden bg-slate-200`}>
        <img
          src={imgSrc}
          alt="Profile"
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      </div>
    )
  }

  return (
    <div className={`${sizeClasses[size]} rounded-full flex-shrink-0 group-hover:ring-2 group-hover:ring-[#cfa190] dark:group-hover:ring-[#1e2a2e] transition-all overflow-hidden bg-[#1e2a2e] dark:bg-[#cfa190]`}>
      <div className="w-full h-full flex items-center justify-center">
        <span className="font-semibold text-[#cfa190] dark:text-[#1e2a2e]">{initial}</span>
      </div>
    </div>
  )
}

const Sidebar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout: authLogout } = useAuth()

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Leads badge counts — admin/MD see unassigned (triage inbox);
  // reservation_manager sees their own open leads. Hidden when 0. Refreshes every 60s.
  const [unassignedLeadCount, setUnassignedLeadCount] = useState(0)
  const [myLeadCount, setMyLeadCount] = useState(0)
  // Calendar urgent-alerts badge (Phase 7): overdue/due-today operational items + open tasks.
  const [calendarAlertCount, setCalendarAlertCount] = useState(0)
  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    const load = async () => {
      try {
        if (isManagement(user)) {
          const res = await listLeads({ assignedAgentId: 'unassigned', limit: 1 })
          if (!cancelled) setUnassignedLeadCount(res.data?.total || 0)
        } else if (isReservationManager(user)) {
          const res = await listLeads({ assignedAgentId: user.id, limit: 1 })
          if (!cancelled) setMyLeadCount(res.data?.total || 0)
        }
      } catch {
        /* non-blocking — a failed count shouldn't break the sidebar */
      }
    }
    load()
    const interval = setInterval(load, 60000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [user?.id, user?.role])

  // Calendar alert badge — urgent operational items from /api/agenda. Fail-quiet:
  // a failed count must never block the sidebar render or show an error.
  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    const load = () => getCalendarAlertSummary()
      .then((s) => { if (!cancelled) setCalendarAlertCount(s.total || 0) })
      .catch(() => { /* non-blocking — never break the sidebar */ })
    load()
    const interval = setInterval(load, 60000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [user?.id])

  // Section open/close states
  const [exclusiveVillasOpen, setExclusiveVillasOpen] = useState(
    location.pathname.startsWith('/exclusive') ||
    location.pathname.startsWith('/guest-preferences') ||
    (location.pathname.includes('/villas/') && location.pathname.includes('/dashboard'))
  )
  const [nonExclusiveVillasOpen, setNonExclusiveVillasOpen] = useState(
    location.pathname.startsWith('/non-exclusive') || location.pathname === '/non-exclusive-villas'
  )
  const [villasOpen, setVillasOpen] = useState(
    (location.pathname.startsWith('/villas') && !location.pathname.startsWith('/villas/')) ||
    location.pathname.startsWith('/owners') ||
    location.pathname.startsWith('/agents')
  )
  const [contactsOpen, setContactsOpen] = useState(
    location.pathname.startsWith('/owners') ||
    location.pathname.startsWith('/agents') ||
    location.pathname.startsWith('/agencies') ||
    location.pathname.startsWith('/vendors')
  )
  const [financialsOpen, setFinancialsOpen] = useState(
    location.pathname.startsWith('/owner-payouts') ||
    location.pathname.startsWith('/transactions') ||
    location.pathname.startsWith('/agent-commissions') ||
    location.pathname.startsWith('/reports-export')
  )
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith('/settings')
  )
  const [crmOpen, setCrmOpen] = useState(
    location.pathname.startsWith('/clients') || location.pathname.startsWith('/leads')
  )

  const [exclusiveVillas, setExclusiveVillas] = useState([])

  // Permission-based access control
  const permissions = user?.permissions || []
  const hasPermission = (perm) => permissions.includes(perm)

  // Role checks for backwards compatibility
  const isAdmin = user?.role === ROLES.ADMIN
  const isManagingDirector = user?.role === ROLES.MANAGING_DIRECTOR
  // NOTE: named *Role, not `isReservationManager` — that name would shadow the
  // imported isReservationManager() helper (constants/roles) that the lead-count
  // effect above calls as a function. The old shadow turned that call into a
  // TypeError (silently swallowed by the effect's catch), so a reservation
  // manager never received her "My Leads" count.
  const isReservationManagerRole = isReservationAgentRole(user)
  const reservationAgentLabels = getReservationAgentLabels()

  // Legacy role names removed — actual DB roles are admin / managing_director / reservation_manager.
  // Kept for backward-compat with UI code still referencing old names.
  const isDirector = isAdmin || isManagingDirector
  const isManager = isManagingDirector
  const isAgent = isReservationManagerRole
  const isFinance = isAdmin || isManagingDirector

  // Email Signature is a personal setting, not a system one. It must render for
  // exactly the roles the /settings/email-signature route admits
  // (OPERATIONAL_ROLES), independent of `canAccessSettings` — a reservation
  // manager gets AgentSidebarContent, which has no Settings section at all, and
  // reservationAgentAccess.js already whitelists this path for that role.
  const canUseEmailSignature = hasRole(user, OPERATIONAL_ROLES)

  // Deferred: NavItem is declared further down this component body, so building
  // the element eagerly here would hit its temporal dead zone.
  const renderEmailSignatureNavItem = () =>
    canUseEmailSignature ? (
      <NavItem path="/settings/email-signature" label="Email Signature" icon={PenLine} />
    ) : null

  // Permission-based access (falls back to role checks for compatibility)
  const canAccessSettings = hasPermission('settings') || isDirector
  const canAccessImport = hasPermission('import') || isDirector || isManagingDirector
  const canAccessReports = hasPermission('reports') || isDirector || isManager || isFinance
  const canAccessFinancials = hasPermission('financials') || isDirector || isManager || isFinance
  const canAccessVillas = hasPermission('villas') || !isAgent
  const canAccessUsers = hasPermission('users') || isAdmin

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const handleOpenMobileMenu = () => setMobileMenuOpen(true)
    window.addEventListener('ve:open-mobile-menu', handleOpenMobileMenu)
    document.addEventListener('ve:open-mobile-menu', handleOpenMobileMenu)

    return () => {
      window.removeEventListener('ve:open-mobile-menu', handleOpenMobileMenu)
      document.removeEventListener('ve:open-mobile-menu', handleOpenMobileMenu)
    }
  }, [])

  // Pending approvals count: rate requests + contact/villa change requests
  // (admin sees all; employees see their own)
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0)
  useEffect(() => {
    if (!user) return
    let cancelled = false
    const fetchCount = async () => {
      try {
        const api = (await import('../services/api')).default
        const [rateRes, crRes] = await Promise.allSettled([
          api.get('/rate-requests?status=submitted,push_failed'),
          api.get('/change-requests?status=submitted'),
        ])
        const rateCount = rateRes.status === 'fulfilled' ? (rateRes.value.data || []).length : 0
        const crCount = crRes.status === 'fulfilled' ? (crRes.value.data || []).length : 0
        if (!cancelled) setPendingApprovalsCount(rateCount + crCount)
      } catch (_) { /* ignore */ }
    }
    fetchCount()
    const interval = setInterval(fetchCount, 60000) // refresh every minute
    return () => { cancelled = true; clearInterval(interval) }
  }, [user?.id, isAdmin, location.pathname])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflowY = 'hidden'
    } else {
      document.body.style.overflowY = ''
    }
    return () => {
      document.body.style.overflowY = ''
    }
  }, [mobileMenuOpen])

  // Auto-open sections based on current route
  useEffect(() => {
    if (location.pathname.startsWith('/exclusive') ||
      location.pathname.startsWith('/guest-preferences') ||
      (location.pathname.includes('/villas/') && location.pathname.includes('/dashboard'))) {
      setExclusiveVillasOpen(true)
    }
    if (location.pathname.startsWith('/non-exclusive') || location.pathname === '/non-exclusive-villas') {
      setNonExclusiveVillasOpen(true)
    }
    if ((location.pathname.startsWith('/villas') && !location.pathname.startsWith('/villas/')) ||
      location.pathname.startsWith('/owners') ||
      location.pathname.startsWith('/agents')) {
      setVillasOpen(true)
    }
    if (location.pathname.startsWith('/owners') ||
      location.pathname.startsWith('/agents') ||
      location.pathname.startsWith('/agencies') ||
      location.pathname.startsWith('/vendors')) {
      setContactsOpen(true)
    }
    if (location.pathname.startsWith('/owner-payouts') ||
      location.pathname.startsWith('/transactions') ||
      location.pathname.startsWith('/agent-commissions') ||
      location.pathname.startsWith('/reports-export')) {
      setFinancialsOpen(true)
    }
    if (location.pathname.startsWith('/settings')) {
      setSettingsOpen(true)
    }
    if (location.pathname.startsWith('/clients') || location.pathname.startsWith('/leads')) {
      setCrmOpen(true)
    }
  }, [location.pathname])

  // Load exclusive villas
  useEffect(() => {
    let isMounted = true

    async function loadExclusiveVillas() {
      try {
        const { data } = await getVillaPickerOptions()
        if (!isMounted) return

        const villasData = data?.data || data || []
        const exclusive = Array.isArray(villasData)
          ? villasData
              .filter(v => v.is_exclusive)
              .sort((a, b) => (a.name || a.villaName || '').localeCompare(b.name || b.villaName || ''))
          : []

        if (isMounted) {
          setExclusiveVillas(exclusive)
        }
      } catch (error) {
        if (!isMounted) return
        if (error.code !== 'ECONNREFUSED' && error.code !== 'ERR_NETWORK') {
          console.error('Error loading exclusive villas:', error)
        }
        if (isMounted) {
          setExclusiveVillas([])
        }
      }
    }

    if (canAccessVillas) {
      loadExclusiveVillas()
    }

    return () => {
      isMounted = false
    }
  }, [canAccessVillas])

  const handleLogout = () => {
    authLogout()
    navigate('/login')
  }

  const hideFloatingMobileMenuButton = location.pathname.startsWith('/live-chats')

  // Navigation Section Component
  const NavSection = ({ title, icon: Icon, children, isOpen, onToggle }) => (
    <div className="mt-1">
      <button
        onClick={onToggle}
        className={`
          w-full min-h-[44px] px-4 py-2.5 text-left flex items-center justify-between
          text-xs font-semibold uppercase tracking-wider
          transition-colors duration-200 rounded-lg mx-2
          ${isOpen
            ? 'text-[#1e2a2e] dark:text-white bg-slate-100 dark:bg-slate-800'
            : 'text-slate-500 dark:text-slate-400 hover:text-[#1e2a2e] dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800'
          }
        `}
        style={{ width: 'calc(100% - 16px)' }}
      >
        <span className="flex items-center gap-2">
          {Icon && <Icon size={14} />}
          {title}
        </span>
        <ChevronDown
          size={14}
          className={`transform transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
        />
      </button>
      <div
        className={`
          overflow-hidden transition-all duration-200
          ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div className="py-1 space-y-0.5">
          {children}
        </div>
      </div>
    </div>
  )

  // Navigation Item Component
  const NavItem = ({ path, label, icon: Icon, isNested = false, isActive = false, badge = null }) => {
    const active = isActive || location.pathname === path
    return (
      <Link
        to={path}
        className={`
          flex items-center gap-3 min-h-[44px] px-4 py-2.5 mx-2 rounded-lg
          text-sm font-medium transition-all duration-200
          ${isNested ? 'ml-4' : ''}
          ${active
            ? 'bg-[#cfa190] text-white shadow-sm'
            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-[#1e2a2e] dark:hover:text-white'
          }
        `}
        style={{ width: isNested ? 'calc(100% - 24px)' : 'calc(100% - 16px)' }}
      >
        {Icon && <Icon size={16} color={active ? '#ffffff' : '#475569'} className={active ? 'text-white' : ''} />}
        <span className="truncate flex-1">{label}</span>
        {badge != null && badge > 0 && (
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${active ? 'bg-white text-[#cfa190]' : 'bg-[#cfa190] text-white'}`}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </Link>
    )
  }

  // Sidebar content component (shared between desktop and mobile)
  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-5 border-b border-slate-100 dark:border-slate-700">
        <LogoFull />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 space-y-1">
        {/* Dashboard */}
        <NavItem
          path="/"
          label="Dashboard"
          icon={LayoutDashboard}
          isActive={location.pathname === '/'}
        />

        {/* Inbox */}
        <NavItem
          path="/inbox"
          label="Inbox"
          icon={Mail}
          isActive={location.pathname.startsWith('/inbox')}
        />

        {/* Reservation Operations Calendar */}
        <NavItem
          path="/calendar"
          label="Calendar"
          icon={CalendarDays}
          isActive={location.pathname.startsWith('/calendar')}
          badge={calendarAlertCount}
        />

        {/* Approvals queue (rate + contact/villa change requests). Always visible
            here — this tree renders for admin/managing_director; the badge chip
            only shows when the combined pending count is > 0. The reservation-
            agent tree has its own Approvals item (own-requests view). */}
        <NavItem
          path="/approvals"
          label="Approvals"
          icon={Inbox}
          isActive={location.pathname.startsWith('/approvals')}
          badge={pendingApprovalsCount}
        />

        {/* Exclusive Villas Section */}
        {canAccessVillas && (
          <NavSection
            title="Exclusive Villas"
            icon={Building2}
            isOpen={exclusiveVillasOpen}
            onToggle={() => setExclusiveVillasOpen(!exclusiveVillasOpen)}
          >
            <NavItem
              path="/exclusive-dashboard"
              label="Bookings Dashboard"
              icon={LayoutDashboard}
              isNested
            />
            <NavItem
              path="/exclusive-villas"
              label="Villa List"
              icon={Home}
              isNested
            />
            <NavItem
              path="/guest-preferences"
              label="Guest Preferences"
              icon={FileSpreadsheet}
              isNested
              isActive={location.pathname.startsWith('/guest-preferences')}
            />
            {canAccessFinancials && (
              <NavItem
                path="/owner-payouts"
                label="Owner Payouts"
                icon={Wallet}
                isNested
              />
            )}
            {canAccessReports && (
              <NavItem
                path="/exclusive-reports"
                label="Reports"
                icon={FileBarChart}
                isNested
              />
            )}

            {exclusiveVillas.length > 0 && (
              <>
                <div className="mx-6 mt-3 mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Villa Dashboards
                </div>
                {exclusiveVillas.map((villa) => (
                  <NavItem
                    key={`villa-dash-${villa.id}`}
                    path={`/villas/${villa.id}/dashboard`}
                    label={villa.name || villa.villaName || `Villa #${villa.id}`}
                    icon={Building2}
                    isNested
                  />
                ))}
              </>
            )}
          </NavSection>
        )}

        {/* Non-Exclusive Villas Section */}
        {canAccessVillas && (
          <NavSection
            title="Non-Exclusive"
            icon={Home}
            isOpen={nonExclusiveVillasOpen}
            onToggle={() => setNonExclusiveVillasOpen(!nonExclusiveVillasOpen)}
          >
            <NavItem
              path="/non-exclusive-bookings-dashboard"
              label="Bookings Dashboard"
              icon={LayoutDashboard}
              isNested
            />
            <NavItem
              path="/non-exclusive-villas"
              label="Villa List"
              icon={Home}
              isNested
            />
            {canAccessFinancials && (
              <NavItem
                path="/non-exclusive-payouts"
                label="Owner/Agent Payouts"
                icon={Wallet}
                isNested
              />
            )}
            {canAccessReports && (
              <NavItem
                path="/non-exclusive-reports"
                label="Reports"
                icon={FileBarChart}
                isNested
              />
            )}
          </NavSection>
        )}

        {/* CRM Section */}
        <NavSection
          title="CRM"
          icon={Users}
          isOpen={crmOpen}
          onToggle={() => setCrmOpen(!crmOpen)}
        >
          <NavItem
            path="/clients"
            label="Clients"
            icon={Users}
            isNested
          />
          <NavItem
            path="/leads"
            label="Leads"
            icon={Target}
            isNested
            badge={unassignedLeadCount || myLeadCount}
          />
          <NavItem
            path="/live-chats"
            label="Live Chats"
            icon={MessageCircle}
            isNested
          />
        </NavSection>

        {/* Villas Section */}
        {canAccessVillas && (
          <NavSection
            title="Property Management"
            icon={Home}
            isOpen={villasOpen}
            onToggle={() => setVillasOpen(!villasOpen)}
          >
            <NavItem
              path="/villas"
              label="All Villas"
              icon={Home}
              isNested
            />
            <NavItem
              path="/owners"
              label="Owners"
              icon={User}
              isNested
            />
            <NavItem
              path="/agencies"
              label="Agencies"
              icon={Building2}
              isNested
            />
            <NavItem
              path="/agents"
              label="Agents"
              icon={Briefcase}
              isNested
            />
            <NavItem
              path="/vendors"
              label="Vendors"
              icon={Briefcase}
              isNested
            />
            <NavItem
              path="/duplicates"
              label="Find duplicates"
              icon={Briefcase}
              isNested
            />
            <NavItem
              path="/data-quality"
              label="Data quality"
              icon={Briefcase}
              isNested
            />
            <NavItem
              path="/favorites"
              label="Favorites"
              icon={Briefcase}
              isNested
            />
            <NavItem
              path="/villa-research"
              label="Villa research"
              icon={Briefcase}
              isNested
            />
          </NavSection>
        )}

        {/* Financials Section */}
        {canAccessFinancials && (
          <NavSection
            title="Financials"
            icon={Wallet}
            isOpen={financialsOpen}
            onToggle={() => setFinancialsOpen(!financialsOpen)}
          >
            <NavItem
              path="/transactions"
              label="All Transactions"
              icon={Receipt}
              isNested
            />
            <NavItem
              path="/global-owner-payouts"
              label="Owner Payouts (Global)"
              icon={Wallet}
              isNested
            />
            <NavItem
              path="/agent-commissions"
              label="Agent Commissions"
              icon={CreditCard}
              isNested
            />
            {canAccessReports && (
              <NavItem
                path="/reports-export"
                label="Export Reports"
                icon={Download}
                isNested
              />
            )}
          </NavSection>
        )}

        {/* Settings Section */}
        {canAccessSettings && (
          <NavSection
            title="Settings"
            icon={Settings}
            isOpen={settingsOpen}
            onToggle={() => setSettingsOpen(!settingsOpen)}
          >
            <NavItem
              path="/settings/branding"
              label="Branding"
              icon={Palette}
              isNested
            />
            <NavItem
              path="/settings/templates"
              label="Rate Templates"
              icon={FileSpreadsheet}
              isNested
            />
            {isDirector && (
              <NavItem
                path="/settings/wp-sync"
                label="WordPress Sync"
                icon={RefreshCw}
                isNested
              />
            )}
            <NavItem
              path="/admin/contract-clauses"
              label="Contract Clauses"
              icon={FileSpreadsheet}
              isNested
            />
            <NavItem
              path="/admin/contract-templates"
              label="Contract Templates"
              icon={FileSpreadsheet}
              isNested
            />
            {canAccessUsers && (
              <NavItem
                path="/settings/users"
                label="User Management"
                icon={Users}
                isNested
              />
            )}
            {isAdmin && (
              <NavItem
                path="/admin/audit-log"
                label="Audit Log"
                icon={ScrollText}
                isNested
              />
            )}
            <NavItem
              path="/settings/system"
              label="System"
              icon={Settings}
              isNested
            />
          </NavSection>
        )}

        {/* Import - Director only */}
        {canAccessImport && (
          <NavItem
            path="/import"
            label="Data Import"
            icon={Download}
          />
        )}

        {/* Personal account setting — sits beside the profile block, not in Settings. */}
        {renderEmailSignatureNavItem()}
      </nav>

      {/* User & Logout */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
        <Link
          to="/profile"
          className="flex items-center gap-3 px-3 py-2 mb-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer group"
        >
          <UserAvatar user={user} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#1e2a2e] dark:text-white truncate">{user?.full_name || user?.name || 'User'}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate capitalize">{user?.role?.replace('_', ' ') || 'Staff'}</p>
          </div>
          <ChevronRight size={16} className="text-slate-400 group-hover:text-[#cfa190] transition-colors" />
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
    </>
  )

  // Reservation Agent workspace sidebar content
  const AgentSidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-5 border-b border-slate-100 dark:border-slate-700">
        <LogoFull />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <NavItem
          path="/dashboard"
          label={reservationAgentLabels.dashboard}
          icon={LayoutDashboard}
          isActive={location.pathname === '/' || location.pathname.startsWith('/dashboard')}
        />
        <NavItem
          path="/inbox"
          label={reservationAgentLabels.inbox}
          icon={Mail}
          isActive={location.pathname.startsWith('/inbox')}
        />
        <NavItem
          path="/calendar"
          label={reservationAgentLabels.calendar}
          icon={CalendarDays}
          isActive={location.pathname.startsWith('/calendar')}
          badge={calendarAlertCount}
        />

        <NavSection
          title="Reservations"
          icon={Users}
          isOpen={crmOpen}
          onToggle={() => setCrmOpen(!crmOpen)}
        >
          <NavItem path="/leads" label={reservationAgentLabels.leads} icon={Target} isNested badge={myLeadCount} />
          <NavItem path="/live-chats" label={reservationAgentLabels.chats} icon={MessageCircle} isNested />
          <NavItem
            path="/guest-preferences"
            label="Guest Preferences"
            icon={FileSpreadsheet}
            isNested
            isActive={location.pathname.startsWith('/guest-preferences')}
          />
        </NavSection>

        <NavSection
          title={reservationAgentLabels.bookingsSection}
          icon={CalendarDays}
          isOpen={exclusiveVillasOpen || nonExclusiveVillasOpen}
          onToggle={() => {
            const nextOpen = !(exclusiveVillasOpen || nonExclusiveVillasOpen)
            setExclusiveVillasOpen(nextOpen)
            setNonExclusiveVillasOpen(nextOpen)
          }}
        >
          <NavItem path="/exclusive-dashboard" label="Exclusive Bookings" icon={LayoutDashboard} isNested />
          <NavItem path="/non-exclusive-bookings-dashboard" label="Non-Exclusive Bookings" icon={LayoutDashboard} isNested />
        </NavSection>

        <NavSection
          title={reservationAgentLabels.villaInventory}
          icon={Home}
          isOpen={villasOpen}
          onToggle={() => setVillasOpen(!villasOpen)}
        >
          <NavItem path="/villas" label="All Villas" icon={Home} isNested />
        </NavSection>

        <NavSection
          title={reservationAgentLabels.contacts}
          icon={Briefcase}
          isOpen={contactsOpen}
          onToggle={() => setContactsOpen(!contactsOpen)}
        >
          <NavItem path="/clients" label="Clients" icon={Users} isNested />
          <NavItem path="/agents" label="Agents" icon={Briefcase} isNested />
          <NavItem path="/agencies" label="Agencies" icon={Building2} isNested />
          <NavItem path="/owners" label="Owners" icon={User} isNested />
          <NavItem path="/vendors" label="Vendors" icon={Briefcase} isNested />
        </NavSection>

        <NavItem
          path="/approvals"
          label={reservationAgentLabels.approvals}
          icon={Inbox}
          badge={pendingApprovalsCount}
        />
        <NavItem path="/employee-dashboard" label="My Commission" icon={CreditCard} />

        {/* Personal account setting — sits beside the profile block, not in Settings. */}
        {renderEmailSignatureNavItem()}
      </nav>

      {/* User & Logout */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700">
        <Link
          to="/profile"
          className="flex items-center gap-3 px-3 py-2 mb-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer group"
        >
          {/* Same avatar component the main tree uses (resolves user.avatar_url via
              resolveMediaUrl, with its own initials fallback + onError handling) —
              the agent tree previously rendered a generic <User> icon instead. */}
          <UserAvatar user={user} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#1e2a2e] dark:text-white truncate">{user?.full_name || user?.name || 'User'}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
          </div>
          <ChevronRight size={16} className="text-slate-400 group-hover:text-[#cfa190] transition-colors" />
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
    </>
  )

  return (
    <>
      {/* Mobile Menu Button - Fixed at top */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className={`${hideFloatingMobileMenuButton ? 'hidden' : 'lg:hidden'} fixed top-4 left-4 z-40 inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-2 bg-white dark:bg-slate-800 rounded-lg shadow-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors`}
        aria-label="Open menu"
      >
        <Menu size={24} className="text-[#1e2a2e] dark:text-white" />
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50 transition-opacity"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        className={`
          lg:hidden fixed left-0 top-0 h-full w-72 bg-white dark:bg-slate-900 z-50 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Close button */}
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="absolute top-4 right-4 p-2 text-slate-500 hover:text-[#1e2a2e] hover:bg-slate-100 rounded-lg transition-colors"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>

        {isAgent ? <AgentSidebarContent /> : <SidebarContent />}
      </aside>

      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 z-50 flex-col">
        {isAgent ? <AgentSidebarContent /> : <SidebarContent />}
      </aside>
    </>
  )
}

export default Sidebar
